import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'
import {
    beginMfaPasskey,
    MfaApiError,
    mfaErrorKey,
    sendMfaCode,
    verifyMfa,
    type LoginResponse,
    type MFACodeSent,
    type PendingChallenge,
} from '../../lib/mfa'
import { answerMfaWithPasskey, browserSupportsWebAuthn } from '../../lib/passkeys'
import { useFactorName } from './useFactorName'

/**
 * The second step of a sign-in (specs 2026-10-01-03 and -04): every factor
 * the challenge accepts — an authenticator app code, an e-mail or SMS code
 * sent on request, the user's passkey. Shared by the
 * login card (password, SSO, password reset) and the step-up page.
 */
export function MfaChallengeForm({
    org,
    challenge,
    onVerified,
    onCancel,
    cancelLabel,
    passkeysEnabled = true,
}: {
    org: string
    challenge: PendingChallenge
    onVerified: (resp: LoginResponse) => void
    onCancel?: () => void
    cancelLabel?: string
    /** WebAuthn is configured on the server (GET /auth/providers). */
    passkeysEnabled?: boolean
}) {
    const { t } = useTranslation('auth')
    const factorName = useFactorName()
    const factors = challenge.factors
    const [factorKind, setFactorKind] = useState<string>(factors[0]?.kind ?? '')
    const [code, setCode] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)
    // Codes already sent, per factor (each with the challenge token carrying
    // it), and the resend cooldown.
    const [sent, setSent] = useState<Record<string, MFACodeSent>>({})
    const [cooldownUntil, setCooldownUntil] = useState(0)
    const [now, setNow] = useState(Date.now())
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        setFactorKind(challenge.factors[0]?.kind ?? '')
        setSent({})
    }, [challenge])

    const factor = factors.find((f) => f.kind === factorKind)
    const kind = factor?.kind
    const isCodeFactor = kind === 'email' || kind === 'sms'
    const codeSent = kind ? sent[kind] : undefined
    const showCodeEntry = kind === 'totp' || (isCodeFactor && !!codeSent)
    const cooldown = Math.max(0, Math.ceil((cooldownUntil - now) / 1000))

    useEffect(() => {
        if (showCodeEntry) inputRef.current?.focus()
    }, [showCodeEntry, factorKind])

    useEffect(() => {
        if (cooldownUntil <= Date.now()) return
        const id = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(id)
    }, [cooldownUntil])

    const describe = (err: unknown) => t(mfaErrorKey(err instanceof MfaApiError ? err.code : null))

    const finish = async (run: () => Promise<LoginResponse>) => {
        setSubmitting(true)
        setError(null)
        try {
            onVerified(await run())
        } catch (err) {
            setError(describe(err))
            setCode('')
        } finally {
            setSubmitting(false)
        }
    }

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!code.trim()) return
        // An e-mail or SMS code answers through the token that carries it.
        const challengeToken = codeSent?.challenge_token ?? challenge.token
        await finish(() => verifyMfa(org, { challengeToken, factor: factorKind, code }))
    }

    const send = async () => {
        if (!factor || !isCodeFactor) return
        const codeKind = factor.kind as 'email' | 'sms'
        setSubmitting(true)
        setError(null)
        try {
            const result = await sendMfaCode(org, challenge.token, codeKind)
            setSent((prev) => ({ ...prev, [codeKind]: result }))
            setCooldownUntil(Date.now() + result.resend_after * 1000)
            setNow(Date.now())
            setCode('')
        } catch (err) {
            if (err instanceof MfaApiError && err.retryAfter) {
                setCooldownUntil(Date.now() + err.retryAfter * 1000)
                setNow(Date.now())
            }
            setError(describe(err))
        } finally {
            setSubmitting(false)
        }
    }

    const answerWithPasskey = async () => {
        if (!browserSupportsWebAuthn()) {
            setError(t('passkeys.notSupported'))
            return
        }
        setSubmitting(true)
        setError(null)
        try {
            const { session, credential } = await answerMfaWithPasskey(() => beginMfaPasskey(org, challenge.token))
            onVerified(await verifyMfa(org, { challengeToken: challenge.token, factor: 'passkey', passkeySession: session, credential }))
        } catch (err) {
            // A cancelled browser prompt is not an API error.
            setError(err instanceof MfaApiError ? describe(err) : t('mfa.errors.passkey_failed'))
        } finally {
            setSubmitting(false)
        }
    }

    const description = () => {
        switch (kind) {
            case 'totp':
                return t('mfa.challenge.description')
            case 'email':
            case 'sms':
                return codeSent
                    ? t('mfa.challenge.code_sent', { destination: codeSent.destination })
                    : t(`mfa.challenge.${kind}_description`, { destination: factor?.destination ?? '' })
            case 'passkey':
                return t('mfa.challenge.passkey_description')
            default:
                return ''
        }
    }

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="mfa-challenge-form">
            {factors.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="mfa-no-usable-factor">
                    {t('mfa.challenge.no_usable_factor')}
                </p>
            )}

            {factors.length > 1 && (
                <div className="space-y-2">
                    <Label>{t('mfa.challenge.factor_label')}</Label>
                    <RadioGroup
                        value={factorKind}
                        onValueChange={(v) => { setFactorKind(v); setCode(''); setError(null) }}
                        data-testid="mfa-factor-picker"
                    >
                        {factors.map((f) => (
                            <div key={f.kind} className="flex items-center gap-2">
                                <RadioGroupItem value={f.kind} id={`mfa-factor-${f.kind}`} data-testid={`mfa-factor-${f.kind}`} />
                                <Label htmlFor={`mfa-factor-${f.kind}`} className="font-normal">
                                    {factorName(f)}
                                </Label>
                            </div>
                        ))}
                    </RadioGroup>
                </div>
            )}

            {kind && (
                <p className="text-sm text-muted-foreground" data-testid="mfa-challenge-description">
                    {description()}
                </p>
            )}

            {kind === 'passkey' && (
                passkeysEnabled ? (
                    <Button type="button" className="w-full" onClick={answerWithPasskey} disabled={submitting} data-testid="mfa-use-passkey">
                        {submitting ? t('mfa.challenge.verifying') : t('mfa.challenge.use_passkey')}
                    </Button>
                ) : (
                    <p className="text-sm text-muted-foreground" data-testid="mfa-passkey-unavailable">{t('mfa.challenge.passkey_unavailable')}</p>
                )
            )}

            {isCodeFactor && !codeSent && (
                <Button type="button" className="w-full" onClick={send} disabled={submitting || cooldown > 0} data-testid="mfa-send-code">
                    {cooldown > 0 ? t('mfa.challenge.resend_in', { seconds: cooldown }) : t('mfa.challenge.send_code')}
                </Button>
            )}

            {showCodeEntry && (
                <>
                    <div className="space-y-2">
                        <Label htmlFor="mfa-code">{t('mfa.challenge.code_label')}</Label>
                        <Input
                            ref={inputRef}
                            id="mfa-code"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="123456"
                            maxLength={7}
                            className="font-mono tracking-widest"
                            data-testid="mfa-code-input"
                        />
                    </div>
                    <Button type="submit" className="w-full" disabled={submitting || !code.trim()} data-testid="mfa-submit">
                        {submitting ? t('mfa.challenge.verifying') : t('mfa.challenge.submit')}
                    </Button>
                    {isCodeFactor && (
                        <button
                            type="button"
                            className="block w-full text-center text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 disabled:no-underline disabled:opacity-60"
                            onClick={send}
                            disabled={submitting || cooldown > 0}
                            data-testid="mfa-resend-code"
                        >
                            {cooldown > 0 ? t('mfa.challenge.resend_in', { seconds: cooldown }) : t('mfa.challenge.resend')}
                        </button>
                    )}
                </>
            )}

            {error && (
                <div className="text-destructive text-sm bg-destructive/10 border border-destructive/20 p-2 rounded" data-testid="mfa-error">
                    {error}
                </div>
            )}

            <div className="flex flex-col items-center gap-2 text-sm">
                {onCancel && (
                    <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground underline underline-offset-2"
                        onClick={onCancel}
                        data-testid="mfa-cancel"
                    >
                        {cancelLabel ?? t('mfa.challenge.cancel')}
                    </button>
                )}
            </div>
        </form>
    )
}
